/**
 * MapLayerControl - UI control for managing map layers using MapboxAPI abstraction
 *
 * This refactored version delegates all Mapbox-specific operations to the MapboxAPI class,
 * keeping this class focused on UI management and configuration handling.
 *
 * MAPWARPER MOSAIC SUPPORT:
 * -------------------------
 * This class includes static utility methods for handling Mapwarper mosaic URLs.
 * Mosaics are collections of georeferenced maps that can be served as a single TMS layer.
 *
 * Supported URL patterns:
 * - https://mapwarper.net/layers/2054
 * - https://mapwarper.net/layers/2054#Show_tab
 * - https://warper.wmflabs.org/layers/123
 *
 * API endpoints used:
 * - Metadata: https://mapwarper.net/api/v1/layers/{id}
 * - Tiles: https://mapwarper.net/layers/tile/{id}/{z}/{x}/{y}.png
 *
 * Usage:
 * ```javascript
 * // Detect if URL is a mosaic
 * if (MapLayerControl.isMapwarperMosaicUrl(url)) {
 *     // Create config from URL
 *     const config = await MapLayerControl.createConfigFromMapwarperUrl(url);
 *     // Config is ready to use with MapboxAPI
 * }
 * ```
 *
 * The generated config includes:
 * - id: Unique identifier (mapwarper-mosaic-{id})
 * - type: 'tms'
 * - title: Mosaic name from API
 * - description: Mosaic description + maps count
 * - url: TMS tile URL template
 * - bbox: Bounding box coordinates
 * - attribution: Link to original mosaic
 * - opacity: Default 0.85
 *
 * See: https://github.com/timwaters/mapwarper/blob/master/README_API.md
 */
import {LayerSettingsModal} from './layer-settings-modal.js';
import {MapboxAPI} from './mapbox-api.js';
import {DataUtils} from './map-utils.js';
import {MapWarperAPI} from './mapwarper-url-api.js';
import {LayerOrderManager} from './layer-order-manager.js';
import {MapContextMessagesControl, LOADING_ICON_HTML} from './map-context-messages-control.js';
import {LayerThumbnail} from './layer-thumbnail.js';

export class MapLayerControl {
    constructor(options) {
        // Handle options structure for groups and configuration
        if (Array.isArray(options)) {
            this._state = { groups: options };
            this._config = {};
        } else if (options && options.groups) {
            this._state = { groups: options.groups };
            this._config = options;
        } else {
            this._state = { groups: [options] };
            this._config = {};
        }

        this._instanceId = (MapLayerControl.instances || 0) + 1;
        MapLayerControl.instances = this._instanceId;
        this._initialized = false;
        this._sourceControls = [];

        this._legendImageCache = new Map();// Cache for loaded legend images to avoid reloading
        this._mapboxAPI = null;// MapboxAPI instance will be initialized when map is available
        this._defaultStyles = {};// Initialize default styles (will be populated by _loadDefaultStyles)
        this._layerSettingsModal = null;
        this._stateManager = null;
        this._filterActiveMaps = false;
        this._filterMapsInView = false;
        this._mapMoveListenerAdded = false;
        this._initializingLayers = false;

        // Load default styles asynchronously
        this._loadDefaultStyles();
    }

    /**
     * Initialize the control with map and container
     */
    async renderToContainer(container, map) {
        this._containerRef = container;
        this._container = $(container)[0];
        this._map = map;
        this._initialized = false;

        // Make sure default styles are loaded BEFORE creating MapboxAPI
        await this._ensureDefaultStylesLoaded();

        // Load all layers from registry and add them to state
        this._loadAllLayersFromRegistry();

        // Initialize MapboxAPI with the map and atlas configuration
        this._mapboxAPI = new MapboxAPI(map, {
            styles: this._defaultStyles,
            orderedGroups: this._state.groups
        });

        // Initialize layer settings modal
        this._layerSettingsModal = new LayerSettingsModal(this);

        // Add drawer focus management to prevent aria-hidden accessibility issues
        this._setupDrawerFocusManagement();

        const initOnce = async () => {
            if (this._initialized) return;
            this._initialized = true;
            await this._initializeControl(container);
            this._initializeFilterControls();
        };

        // Initialize the control UI
        if (this._map.isStyleLoaded()) {
            initOnce();
        } else {
            const fallbackTimeout = setTimeout(() => {
                if (this._map.getStyle()) {
                    initOnce();
                }
            }, 1000);

            this._map.once('style.load', () => {
                clearTimeout(fallbackTimeout);
                initOnce();
            });
        }

        $(container).append($('<div>', { class: 'layer-control' }));
    }

    /**
     * The container may not exist yet when renderToContainer runs (some pages
     * mount the layer panel later, others never mount one), so resolve it on
     * demand instead of trusting the reference captured at startup.
     */
    _getContainer() {
        if (!this._container && this._containerRef) {
            this._container = $(this._containerRef)[0];
        }
        return this._container;
    }

    /**
     * Load all layers from the registry and add them to state
     */
    _loadAllLayersFromRegistry() {
        if (!window.layerRegistry || !window.layerRegistry._initialized) {
            console.warn('[LayerControl] Layer registry not initialized, cannot load all layers');
            return;
        }

        const currentLayerIds = new Set(this._state.groups.map(g => g.id));
        const allLayers = [];

        // Get all layers from all atlases
        window.layerRegistry._registry.forEach((layer, layerId) => {
            // Skip if already in current atlas
            if (!currentLayerIds.has(layerId) && !currentLayerIds.has(layer._originalId)) {
                allLayers.push({
                    ...layer,
                    id: layerId,
                    initiallyChecked: false
                });
            }
        });

        // Add all layers to state
        this._state.groups = [...this._state.groups, ...allLayers];
    }

    /**
     * Load default styles configuration
     */
    async _loadDefaultStyles() {
        try {
            const defaultsResponse = await fetch(window.amche.LAYER_DEFAULTS);
            const configResponse = await fetch(window.amche.DEFAULT_ATLAS);

            if (!defaultsResponse.ok || !configResponse.ok) {
                throw new Error('Failed to load configuration files');
            }

            const defaults = await defaultsResponse.json();
            const config = await configResponse.json();

            this._defaultStyles = defaults.layer.style || {};
            if (config.styles) {
                this._defaultStyles = DataUtils.deepMerge(config.styles, this._defaultStyles) || {};
            }

        } catch (error) {
            console.error('Error loading default styles:', error);
        }
    }

    /**
     * Ensure default styles are loaded
     */
    async _ensureDefaultStylesLoaded() {
        // Ensure _defaultStyles exists and has content
        if (this._defaultStyles && typeof this._defaultStyles === 'object' && Object.keys(this._defaultStyles).length > 0) {
            return;
        }
        await this._loadDefaultStyles();
    }

    /**
     * Update state with new configuration
     */
    async _updateState(newState) {
        this._state = {
            ...this._state,
            groups: newState.groups.map(newGroup => {
                const existingGroup = this._state.groups.find(g => g.id === newGroup.id);
                return existingGroup ? { ...existingGroup, ...newGroup } : newGroup;
            })
        };

        // Update the ordered groups in MapboxAPI
        if (this._mapboxAPI) {
            this._mapboxAPI._orderedGroups = this._state.groups;
        }

        this._cleanupLayers();
        await this._rebuildUI();
    }

    /**
     * Clean up existing layers using MapboxAPI
     */
    _cleanupLayers() {
        if (!this._mapboxAPI) return;

        // Remove all custom layers and sources using MapboxAPI
        this._state.groups.forEach(group => {
            this._mapboxAPI.removeLayerGroup(group.id, group);
        });
    }

    /**
     * Rebuild the UI
     */
    async _rebuildUI() {
        if (this._container) {
            this._container.innerHTML = '';
            this._sourceControls = [];
            await this._initializeControl(this._container);
        }
    }

    /**
     * Initialize the main control UI
     */
    async _initializeControl(container) {
        // Add current atlas layers
        this._state.groups.forEach((group, groupIndex) => {
            $(container).append(this._createGroupHeader(group, groupIndex));
        });

        // Initialize all layers explicitly after UI is set up (async to prevent blocking)
        await this._initializeAllLayers();

        if (!this._initialized) {
            this._initializeWithAnimation();
        }
    }

    /**
     * Initialize all layers to their proper visibility states
     * Layers in _state.groups are in visual order (first = top visually)
     * We need to add them to the map in rendering order (reversed, basemaps first)
     */
    async _initializeAllLayers() {
        this._initializingLayers = true;
        // Collect initially checked layers with their indices
        const initiallyCheckedLayers = [];
        this._state.groups.forEach((group, groupIndex) => {
            if (group.initiallyChecked) {
                initiallyCheckedLayers.push({ group, groupIndex });
            }
        });

        // Convert to map rendering order using centralized logic
        const layersOnly = initiallyCheckedLayers.map(item => item.group);
        const mapOrderLayers = LayerOrderManager.urlOrderToMapOrder(layersOnly);

        // Create a map of layer id to group index for quick lookup
        const layerIdToIndex = new Map();
        initiallyCheckedLayers.forEach(item => {
            layerIdToIndex.set(item.group.id, item.groupIndex);
        });

        // Add layers progressively with yields to browser
        for (const layer of mapOrderLayers) {
            const groupIndex = layerIdToIndex.get(layer.id);
            if (groupIndex !== undefined) {
                const layerTitle = this._escapeHtml(layer.title || layer.id);
                // A message may already be queued for this layer from map-init.js -
                // shown before URL layers (e.g. "osm:..." dynamic shorthands) were even
                // resolved. Reuse it (refreshing the text, since the placeholder shown
                // while queued may not have known the real title yet) instead of
                // creating a second one. No thumbnail while loading - just the spinner
                // and name; the real thumbnail appears once the layer is actually added.
                const messageId = MapContextMessagesControl.show(
                    `${LOADING_ICON_HTML}<strong>${layerTitle}</strong>`,
                    { id: layer._loadingMessageId }
                );
                // createLayerGroup only issues the addSource/addLayer calls - it resolves
                // almost instantly even though tiles are still loading in the background.
                // Enforce a minimum visible time so the message is actually perceivable
                // instead of flashing for a few milliseconds on fast/cached layers.
                const minVisible = new Promise(resolve => setTimeout(resolve, 400));
                await Promise.all([this._toggleLayerGroup(groupIndex, true), minVisible]);
                // Swap the spinner+name for the layer's thumbnail (with a zoom shortcut
                // when a bbox is known) rather than closing outright, so newly added maps -
                // e.g. from the layer creator - give visible confirmation instead of
                // just silently appearing.
                const labelHtml = this._buildLayerLabelHTML(layer, layerTitle);
                const zoomLink = this._buildZoomToLayerLink(layer);
                MapContextMessagesControl.show(
                    `${labelHtml}${zoomLink ? ' &middot; ' + zoomLink : ''}`,
                    { id: messageId }
                );
                setTimeout(() => MapContextMessagesControl.close(messageId), 3000);
                // Yield to browser between layers to prevent blocking
                await new Promise(resolve => requestAnimationFrame(resolve));
            }
        }

        // Hide non-initially-checked style layers (they exist in the Mapbox style by default
        // and must be explicitly hidden; other layer types are never added so no action needed)
        this._state.groups.forEach((group, groupIndex) => {
            if (!group.initiallyChecked && group.type === 'style') {
                if (!this._map.getStyle()) {
                    this._map.once('style.load', () => {
                        this._toggleLayerGroup(groupIndex, false);
                    });
                } else {
                    this._toggleLayerGroup(groupIndex, false);
                }
            }
        });

        this._initializingLayers = false;
        window.layersInitialized = true;
        window.dispatchEvent(new CustomEvent('layersInitialized'));
    }

    /**
     * Create group header UI element
     */
    _createGroupHeader(group, groupIndex) {
        const $groupHeader = $('<sl-details>', {
            class: 'group-header w-full map-controls-group',
            open: group.initiallyChecked || false
        });

        $groupHeader.attr('data-layer-id', group.id);
        this._sourceControls[groupIndex] = $groupHeader[0];

        // Set up event handlers
        this._setupGroupHeaderEvents($groupHeader, group);

        // Create summary section
        const $summary = this._createGroupSummary(group);
        $groupHeader.append($summary);

        // Add description and attribution
        this._addGroupMetadata($groupHeader, group);

        // Add type-specific content
        this._addTypeSpecificContent($groupHeader, group, groupIndex);

        // If layer is initially checked and expanded, load legend image immediately
        if (group.initiallyChecked && group.legendImage) {
            // Use setTimeout to ensure DOM is fully ready
            setTimeout(() => {
                this._loadLegendImageIfNeeded($groupHeader[0], group.legendImage);
            }, 100);
        }

        return $groupHeader;
    }

    /**
     * Set up group header event handlers
     */
    _setupGroupHeaderEvents($groupHeader, group) {
        $groupHeader[0].addEventListener('sl-show', (event) => {
            this._handleGroupShow(event, group);

            // Load legend image when details panel is expanded (if layer is enabled)
            const toggleInput = event.target.querySelector('.toggle-switch input[type="checkbox"]');
            if (toggleInput && toggleInput.checked && group.legendImage) {
                this._loadLegendImageIfNeeded(event.target, group.legendImage);
            }
        });

        $groupHeader[0].addEventListener('sl-hide', (event) => {
            this._handleGroupHide(event, group);
        });
    }

    /**
     * Resolve a group's current position in `_state.groups`.
     *
     * Positions shift whenever a layer is inserted at runtime (adding a map from
     * the creator splices in at index 0), so handlers must look the index up when
     * they fire rather than reusing one captured when the row was built.
     */
    _getGroupIndex(group) {
        if (!group || !group.id) return -1;
        return this._state.groups.findIndex(g => g.id === group.id);
    }

    /**
     * Handle group show event
     */
    _handleGroupShow(event, group) {
        const toggleInput = event.target.querySelector('.toggle-switch input[type="checkbox"]');

        if (toggleInput && !toggleInput.checked) {
            toggleInput.checked = true;
        }

        // For style layers, sync sublayer states
        if (group.type === 'style' && group.layers) {
            this._syncStyleLayerSubToggles(event.target, group, true);
        }

        // Determine if this is a cross-atlas layer
        const isCrossAtlas = $(event.target).hasClass('cross-atlas-layer');
        const effectiveGroupIndex = isCrossAtlas ? -1 : this._getGroupIndex(group);

        this._toggleLayerGroup(effectiveGroupIndex, true);

        $(event.target).closest('.group-header').addClass('active');

        // Load legend image if it exists and hasn't been loaded yet
        this._loadLegendImageIfNeeded(event.target, group.legendImage);

        // Dispatch custom event for URL sync
        window.dispatchEvent(new CustomEvent('layer-toggled', {
            detail: { layerId: group.id, visible: true, isCrossAtlas: isCrossAtlas }
        }));
    }

    /**
     * Handle group hide event
     */
    _handleGroupHide(event, group) {
        const toggleInput = event.target.querySelector('.toggle-switch input[type="checkbox"]');

        if (toggleInput && toggleInput.checked) {
            toggleInput.checked = false;
        }

        // For style layers, sync sublayer states
        if (group.type === 'style' && group.layers) {
            this._syncStyleLayerSubToggles(event.target, group, false);
        }

        // Determine if this is a cross-atlas layer
        const isCrossAtlas = $(event.target).hasClass('cross-atlas-layer');
        const effectiveGroupIndex = isCrossAtlas ? -1 : this._getGroupIndex(group);

        this._toggleLayerGroup(effectiveGroupIndex, false);

        $(event.target).closest('.group-header').removeClass('active');

        // Dispatch custom event for URL sync
        window.dispatchEvent(new CustomEvent('layer-toggled', {
            detail: { layerId: group.id, visible: false, isCrossAtlas: isCrossAtlas }
        }));
    }

    /**
     * Sync sublayer toggle states for style layers
     */
    _syncStyleLayerSubToggles(groupElement, group, isVisible) {
        const $sublayerToggles = $(groupElement).find('.layer-controls .toggle-switch input[type="checkbox"]');

        if (isVisible) {
            // When showing, set sublayer toggles to match actual layer visibility
            $sublayerToggles.each((index, toggle) => {
                const layer = group.layers[index];
                if (layer) {
                    const actualVisibility = this._getStyleLayerVisibility(layer);
                    $(toggle).prop('checked', actualVisibility);
                }
            });
        } else {
            // When hiding, turn off all sublayer toggles and hide the layers
            $sublayerToggles.prop('checked', false);
            group.layers.forEach(layer => {
                this._handleStyleLayerToggle(layer, false);
            });
        }
    }

    /**
     * Toggle layer group visibility using MapboxAPI
     */
    async _toggleLayerGroup(groupIndex, visible) {
        let group;

        // Handle cross-atlas layers (groupIndex = -1)
        if (groupIndex === -1) {
            // For cross-atlas layers, we need to find the group by the element that triggered this
            const activeElement = document.activeElement;
            const groupElement = activeElement ? activeElement.closest('.group-header') : null;
            if (groupElement) {
                const groupId = groupElement.getAttribute('data-layer-id');
                group = this._allAtlasLayers.find(layer => layer.id === groupId);
            }
            if (!group) {
                console.warn('Could not find cross-atlas layer group');
                return;
            }
        } else {
            group = this._state.groups[groupIndex];
        }

        if (!this._mapboxAPI) {
            console.warn('MapboxAPI not initialized');
            return;
        }

        try {
            // If type, _sourceAtlas, or tags are missing, try to resolve from the registry
            if ((!group.type || !group._sourceAtlas || !group.tags) && group.id && window.layerRegistry) {
                const resolvedLayer = window.layerRegistry.getLayer(group.id);
                if (resolvedLayer) {
                    const updates = {};
                    if (!group.type && resolvedLayer.type) {
                        updates.type = resolvedLayer.type;
                    }
                    if (!group._sourceAtlas && resolvedLayer._sourceAtlas) {
                        updates._sourceAtlas = resolvedLayer._sourceAtlas;
                    }
                    if (!group.tags && resolvedLayer.tags) {
                        updates.tags = resolvedLayer.tags;
                    }
                    // Merge other useful properties from registry
                    if (resolvedLayer._prefixedId) updates._prefixedId = resolvedLayer._prefixedId;
                    if (resolvedLayer._originalId) updates._originalId = resolvedLayer._originalId;

                    if (Object.keys(updates).length > 0) {
                        group = { ...group, ...updates };
                        // Update the group in state so we don't have to resolve it again
                        const stateGroupIndex = this._state.groups.findIndex(g => g.id === group.id);
                        if (stateGroupIndex !== -1) {
                            this._state.groups[stateGroupIndex] = group;
                        }
                        // Re-register with state manager to update the stored config
                        if (this._stateManager && (updates._sourceAtlas || updates.tags)) {
                            this._registerLayerWithStateManager(group);
                        }
                    }
                }
            }

            // Validate that group has required properties
            if (!group.type) {
                return;
            }

            if (visible) {
                // Create or show the layer group
                await this._mapboxAPI.createLayerGroup(group.id, group, { visible: true });

                // Apply initial opacity from config if it exists
                // Note: Pass 1.0 as the opacity value so the multiplier logic in mapbox-api.js
                // correctly applies config.opacity (e.g., 1.0 * 0.44 = 0.44)
                if (group.opacity !== undefined && group.opacity !== 1) {
                    this._mapboxAPI.updateLayerOpacity(group.id, group, 1.0);
                }

                // For style layers, ensure sublayers are properly synchronized
                if (group.type === 'style' && group.layers && this._container) {
                    // Find the group header element to sync sublayer toggles
                    const groupElement = this._container.querySelector(`[data-layer-id="${group.id}"]`);
                    if (groupElement) {
                        // Use a small delay to ensure the main layer is fully processed
                        setTimeout(() => {
                            this._syncStyleLayerSubToggles(groupElement, group, true);
                        }, 50);
                    }
                }

                // Register with state manager if available
                if (this._stateManager) {
                    this._registerLayerWithStateManager(group);
                }

                // Update attribution after layer is added
                if (window.attributionControl) {
                    window.attributionControl._updateAttribution();
                }
            } else {
                // Hide the layer group
                this._mapboxAPI.updateLayerGroupVisibility(group.id, group, false);

                // Unregister with state manager if available
                if (this._stateManager) {
                    this._unregisterLayerWithStateManager(group.id);
                }

                // Update attribution after layer is removed (with small delay to ensure layer is fully removed)
                setTimeout(() => {
                    if (window.attributionControl) {
                        window.attributionControl._updateAttribution();
                    } else {
                        console.warn('[LayerControl] Attribution control not available');
                    }
                }, 50);

                // Trigger layer-toggled event for layer removal
                window.dispatchEvent(new CustomEvent('layer-toggled', {
                    detail: { layerId: group.id, visible: false }
                }));

                // Update URL if urlManager is available
                if (window.urlManager) {
                    setTimeout(() => {
                        window.urlManager.updateURL();
                    }, 50);
                }
            }
        } catch (error) {
            console.error(`Error toggling layer group ${group.id}:`, error);
        }
    }

    /**
     * Get atlas ID for a layer
     */
    _escapeHtml(text) {
        return String(text)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    /**
     * Small non-interactive thumbnail for context messages, mirroring
     * MapAttributionControl._buildThumbnailHTML.
     */
    _buildMessageThumbnailHTML(layer) {
        try {
            const thumb = LayerThumbnail.generate(layer, 16, { interactive: false });
            thumb.style.display = 'inline-block';
            thumb.style.verticalAlign = 'middle';
            thumb.style.marginRight = '6px';
            thumb.style.borderRadius = '4px';
            return thumb.outerHTML;
        } catch (error) {
            console.warn('[MapLayerControl] Failed to build message thumbnail:', error);
            return '';
        }
    }

    /**
     * Thumbnail + bold layer name for context messages, replacing the plain
     * quoted-title format (e.g. Loading map "X") with a MapAttributionControl-style
     * label (thumbnail to the left of a bold name).
     */
    _buildLayerLabelHTML(layer, layerTitle) {
        return `${this._buildMessageThumbnailHTML(layer)}<strong>${layerTitle}</strong>`;
    }

    /**
     * Resolve a bbox to offer a "Zoom to layer" shortcut on: the layer's own
     * bbox if set, otherwise its source atlas's bbox (mirrors the fallback in
     * MapBrowserControl._handleZoomToLayer).
     */
    _getLayerBbox(layer) {
        if (Array.isArray(layer?.bbox) && layer.bbox.length === 4) {
            return layer.bbox;
        }
        const atlasId = layer?._sourceAtlas;
        if (atlasId && window.layerRegistry) {
            const atlasMetadata = window.layerRegistry.getAtlasMetadata(atlasId);
            if (Array.isArray(atlasMetadata?.bbox) && atlasMetadata.bbox.length === 4) {
                return atlasMetadata.bbox;
            }
        }
        return null;
    }

    /**
     * Inline link for context messages that fits the map to a layer's extent.
     * Returns '' when no bbox is known, or when the map is already centered
     * within it (the layer is presumably already in view, so a zoom shortcut
     * would be redundant).
     */
    _buildZoomToLayerLink(layer) {
        const bbox = this._getLayerBbox(layer);
        if (!bbox || !this._isMapCenterOutsideBbox(bbox)) return '';
        return `<a href="#" onclick="window.layerControl?._zoomToBounds(${JSON.stringify(bbox)});return false;">Zoom to layer</a>`;
    }

    /**
     * True when the map's current center falls outside a [west, south, east,
     * north] bbox (or when there's no map yet to check against).
     */
    _isMapCenterOutsideBbox(bbox) {
        if (!this._map || !Array.isArray(bbox) || bbox.length !== 4) return true;
        const [west, south, east, north] = bbox;
        const { lng, lat } = this._map.getCenter();
        return lng < west || lng > east || lat < south || lat > north;
    }

    /**
     * Fit the map to a [west, south, east, north] bbox. Exposed for the inline
     * onclick handlers built by _buildZoomToLayerLink.
     */
    _zoomToBounds(bbox) {
        if (!this._map || !Array.isArray(bbox) || bbox.length !== 4) return;
        const [west, south, east, north] = bbox;
        this._map.fitBounds([[west, south], [east, north]], {
            padding: 50,
            maxZoom: 16,
            duration: 1000
        });
    }

    _getAtlasIdForLayer(group) {
        // First check if group already has _sourceAtlas set
        if (group._sourceAtlas) {
            return group._sourceAtlas;
        }

        // Try to get from layer registry
        if (window.layerRegistry) {
            const resolvedLayer = window.layerRegistry.getLayer(group.id);

            if (resolvedLayer && resolvedLayer._sourceAtlas) {
                return resolvedLayer._sourceAtlas;
            }
        }

        return null;
    }

    /**
     * Add a layer directly from the registry without UI element
     * Used for dynamically adding layers from imported atlases
     */
    async _addLayerDirectly(layerConfig) {
        if (!this._mapboxAPI) {
            throw new Error('MapboxAPI not initialized');
        }

        if (!layerConfig || !layerConfig.id) {
            throw new Error('Invalid layer config');
        }

        try {
            // Layers not known to the registry (e.g. custom layers created via
            // map-creator.html) can't be resolved from a bare ID on reload, so their
            // full config must be preserved verbatim in the URL via `_originalJson`.
            const isKnownToRegistry = window.layerRegistry &&
                !!window.layerRegistry.getLayer(layerConfig.id, null, true);

            const layerWithChecked = {
                ...layerConfig,
                initiallyChecked: true,
                ...(!isKnownToRegistry && { _originalJson: JSON.stringify(layerConfig) })
            };

            let insertPosition;
            if (LayerOrderManager.isBasemap(layerWithChecked)) {
                insertPosition = this._state.groups.findIndex(g => LayerOrderManager.isBasemap(g));
                if (insertPosition === -1) {
                    insertPosition = this._state.groups.length;
                }
            } else {
                insertPosition = 0;
            }

            this._state.groups.splice(insertPosition, 0, layerWithChecked);
            this._insertGroupControl(layerWithChecked, insertPosition);

            // Create the layer on the map
            await this._mapboxAPI.createLayerGroup(layerConfig.id, layerConfig, { visible: true });

            // Apply initial opacity if specified
            if (layerConfig.opacity !== undefined && layerConfig.opacity !== 1) {
                this._mapboxAPI.updateLayerOpacity(layerConfig.id, layerConfig, 1.0);
            }

            // Register with state manager if available
            if (this._stateManager) {
                this._registerLayerWithStateManager(layerConfig);
            }

            // Update attribution
            if (window.attributionControl) {
                window.attributionControl._updateAttribution();
            }

            // Trigger layer-toggled event
            window.dispatchEvent(new CustomEvent('layer-toggled', {
                detail: { layerId: layerConfig.id, visible: true }
            }));

            // Update URL if urlManager is available
            if (window.urlManager) {
                setTimeout(() => {
                    window.urlManager.updateURL();
                }, 50);
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * Build a layer control row for a dynamically added layer and insert it at
     * `insertPosition`, keeping `_sourceControls` index-parallel with
     * `_state.groups`.
     *
     * Without this the two arrays drift apart on every add, and every
     * index-based lookup built on that pairing resolves against the wrong
     * layer — the URL serialiser reads a neighbouring layer's checkbox and
     * drops the added layer from `?layers=`, and toggling one row hides
     * another.
     */
    _insertGroupControl(group, insertPosition) {
        // Reserve the slot before building the row so _createGroupHeader's
        // assignment lands in it rather than overwriting the existing neighbour.
        this._sourceControls.splice(insertPosition, 0, null);

        const $groupHeader = this._createGroupHeader(group, insertPosition);

        // The layer is added already visible, so the row starts switched on.
        const toggleInput = $groupHeader[0].querySelector('.toggle-switch input[type="checkbox"]');
        if (toggleInput) {
            toggleInput.checked = true;
        }
        $groupHeader.addClass('active');

        // Mirror the array position in the DOM, but only when the rows are
        // actually mounted — pages without a layer panel keep them detached, and
        // there the _sourceControls entry above is all that is needed.
        const nextControl = this._sourceControls[insertPosition + 1];
        if (nextControl && nextControl.parentNode) {
            $groupHeader.insertBefore(nextControl);
            return;
        }

        const container = this._getContainer();
        if (container) {
            $(container).append($groupHeader);
        }
    }

    /**
     * Create group summary section
     */
    _createGroupSummary(group) {
        const $summary = $('<div>', {
            slot: 'summary',
            class: 'flex items-center relative w-full h-12 bg-gray-800'
        });

        const $contentWrapper = $('<div>', {
            class: 'flex items-center gap-2 relative z-10 w-full p-2'
        });

        const $toggleTitleContainer = this._createToggleTitle(group);
        $contentWrapper.append($toggleTitleContainer);

        // Add atlas badge (right-aligned)
        const atlasId = this._getAtlasIdForLayer(group);
        if (atlasId) {
            // Capitalize first letter
            const displayName = atlasId.charAt(0).toUpperCase() + atlasId.slice(1);

            // Get color from registry, fallback to blue-600 if not available
            let badgeColor = '#2563eb'; // Default blue
            if (window.layerRegistry) {
                badgeColor = window.layerRegistry.getAtlasColor(atlasId);
            }

            const $atlasBadge = $('<span>', {
                class: 'text-xs text-white px-2 py-1 rounded ml-auto cursor-pointer hover:opacity-80 transition-opacity',
                style: `background-color: ${badgeColor};`,
                text: displayName,
                title: `Switch to ${displayName} atlas`
            });

            // Make badge clickable to navigate to atlas
            $atlasBadge.on('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this._navigateToAtlas(atlasId);
            });

            $contentWrapper.append($atlasBadge);
        }

        // If headerImage is missing, try to resolve from registry
        let headerImage = group.headerImage;
        if (!headerImage && group.id && window.layerRegistry) {
            const resolvedLayer = window.layerRegistry.getLayer(group.id);
            if (resolvedLayer && resolvedLayer.headerImage) {
                headerImage = resolvedLayer.headerImage;
            }
        }

        // Add header background if exists
        if (headerImage) {
            const $headerBg = $('<div>', {
                class: 'absolute top-0 left-0 right-0 w-full h-full bg-cover bg-center bg-no-repeat',
                style: `background-image: url('${headerImage}')`
            });

            const $headerOverlay = $('<div>', {
                class: 'absolute top-0 left-0 right-0 w-full h-full bg-black bg-opacity-40',
                style: 'opacity:0.4'
            });

            $summary.append($headerBg, $headerOverlay, $contentWrapper);
        } else {
            $summary.append($contentWrapper);
        }

        return $summary;
    }

    /**
     * Create toggle title section
     */
    _createToggleTitle(group) {
        const $toggleLabel = $('<label>', { class: 'toggle-switch' });
        const $toggleInput = $('<input>', {
            type: 'checkbox',
            checked: group.initiallyChecked || false
        });
        const $toggleSlider = $('<span>', { class: 'toggle-slider' });

        $toggleLabel.append($toggleInput, $toggleSlider);

        // If no title, try to resolve from registry
        let title = group.title;
        if (!title && group.id && window.layerRegistry) {
            const resolvedLayer = window.layerRegistry.getLayer(group.id);
            if (resolvedLayer && resolvedLayer.title) {
                title = resolvedLayer.title;
            }
        }

        const $titleSpan = $('<span>', {
            text: title || group.id || 'Unknown Layer',
            class: 'control-title text-sm font-medium font-bold text-white'
        });

        const $toggleTitleContainer = $('<div>', {
            class: 'flex items-center gap-2 cursor-pointer'
        });

        $toggleTitleContainer.append($toggleLabel, $titleSpan);
        return $toggleTitleContainer;
    }

    /**
     * Add group metadata (description, attribution)
     */
    _addGroupMetadata($groupHeader, group) {
        // If metadata is missing, try to resolve from registry
        let description = group.description;
        let attribution = group.attribution;

        if ((!description || !attribution) && group.id && window.layerRegistry) {
            const resolvedLayer = window.layerRegistry.getLayer(group.id);
            if (resolvedLayer) {
                description = description || resolvedLayer.description;
                attribution = attribution || resolvedLayer.attribution;
            }
        }

        if (description || attribution) {
            const $contentArea = $('<div>', { class: 'description-area' });

            if (description) {
                const $description = $('<div>', {
                    class: 'text-sm layer-description',
                    html: description
                });
                $contentArea.append($description);
            }

            if (attribution) {
                const $attribution = $('<div>', {
                    class: 'layer-attribution',
                    html: `Source: ${attribution.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ')}`
                });
                $contentArea.append($attribution);
            }

            $groupHeader.append($contentArea);
        }
    }

    /**
     * Add type-specific content
     */
    _addTypeSpecificContent($groupHeader, group, groupIndex) {
        switch (group.type) {
            case 'layer-group':
                this._addLayerGroupContent($groupHeader, group);
                break;
            case 'style':
                this._addStyleLayerContent($groupHeader, group);
                break;

            default:
                // Most layer types don't need special content
                break;
        }

        // Add legend container if available (but don't load image yet)
        if (group.legendImage) {
            const $legendContainer = $('<div>', {
                class: 'legend-container',
                'data-legend-url': group.legendImage,
                'data-legend-loaded': 'false'
            });
            $groupHeader.append($legendContainer);
        }
    }

    /**
     * Add layer group specific content
     */
    _addLayerGroupContent($groupHeader, group) {
        if (!group.groups) return;

        const $radioGroup = $('<div>', { class: 'radio-group mt-2' });

        group.groups.forEach((subGroup, index) => {
            const $radioLabel = this._createRadioOption(subGroup, group, index);
            $radioGroup.append($radioLabel);
        });

        const $contentArea = $('<div>');
        $contentArea.append($radioGroup);
        $groupHeader.append($contentArea);
    }

    /**
     * Create radio option for layer groups
     */
    _createRadioOption(subGroup, parentGroup, index) {
        const $radioLabel = $('<label>', { class: 'radio-label' });
        const $radio = $('<input>', {
            type: 'radio',
            name: `layer-group-${this._instanceId}-${parentGroup.id}`,
            value: subGroup.id,
            checked: index === 0
        });

        $radio.on('change', () => {
            this._handleLayerGroupChange(subGroup.id, parentGroup.groups);
        });

        $radioLabel.append(
            $radio,
            $('<span>', { text: subGroup.title })
        );

        // Add attribution and location links
        if (subGroup.attribution || subGroup.location) {
            const links = [];
            if (subGroup.attribution) {
                links.push(`<a href="${subGroup.attribution}" target="_blank" class="hover:underline">Source</a>`);
            }
            if (subGroup.location) {
                links.push(`<a href="#" class="hover:underline view-link" data-location="${subGroup.location}">View</a>`);
            }

            const $infoDiv = $('<div>', {
                class: 'layer-info text-xs pl-5',
                html: links.join(' | ')
            });

            $infoDiv.find('.view-link').on('click', (e) => {
                e.preventDefault();
                this._flyToLocation(subGroup.location);
            });

            $radioLabel.append($infoDiv);
        }

        return $radioLabel;
    }

    /**
     * Handle layer group change using MapboxAPI
     */
    _handleLayerGroupChange(selectedId, groups) {
        if (!this._mapboxAPI) return;

        // Hide all layers in the group
        groups.forEach(group => {
            this._mapboxAPI.updateLayerGroupVisibility(group.id, group, false);
        });

        // Show selected layer
        const selectedGroup = groups.find(g => g.id === selectedId);
        if (selectedGroup) {
            this._mapboxAPI.updateLayerGroupVisibility(selectedGroup.id, selectedGroup, true);
        }
    }

    /**
     * Add style layer specific content
     */
    _addStyleLayerContent($groupHeader, group) {
        if (!group.layers) return;

        const $layerControls = $('<div>', { class: 'layer-controls mt-3' });

        group.layers.forEach((layer, index) => {
            const $layerControl = this._createStyleLayerControl(layer, group, index);
            $layerControls.append($layerControl);
        });

        $groupHeader.append($layerControls);
    }

    /**
     * Create style layer control
     */
    _createStyleLayerControl(layer, parentGroup, index) {
        const layerId = `sublayer-${parentGroup.id}-${index}`;
        const $layerControl = $('<div>', { class: 'flex items-center gap-2 text-black' });

        const $sublayerToggleLabel = $('<label>', { class: 'toggle-switch' });

        // Check actual layer visibility instead of just parentGroup.initiallyChecked
        const isLayerVisible = this._getStyleLayerVisibility(layer);

        const $sublayerToggleInput = $('<input>', {
            type: 'checkbox',
            id: layerId,
            checked: isLayerVisible
        });
        const $sublayerToggleSlider = $('<span>', { class: 'toggle-slider' });

        $sublayerToggleLabel.append($sublayerToggleInput, $sublayerToggleSlider);

        $sublayerToggleInput.on('change', (e) => {
            this._handleStyleLayerToggle(layer, e.target.checked);
        });

        const $label = $('<label>', {
            for: layerId,
            class: 'text-sm cursor-pointer flex-grow'
        }).text(layer.title);

        $layerControl.append($sublayerToggleLabel, $label);
        return $layerControl;
    }

    /**
     * Get the visibility state of a style layer
     */
    _getStyleLayerVisibility(layer) {
        if (!this._map || !layer.sourceLayer) return false;

        try {
            const styleLayers = this._map.getStyle().layers;
            const matchingLayers = styleLayers.filter(styleLayer =>
                styleLayer['source-layer'] === layer.sourceLayer
            );

            // If any matching layer is visible, consider the layer visible
            return matchingLayers.some(styleLayer => {
                const layerVisibility = this._map.getLayoutProperty(styleLayer.id, 'visibility');
                return layerVisibility !== 'none';
            });
        } catch (error) {
            console.warn('Error checking style layer visibility:', error);
            return false;
        }
    }

    /**
     * Handle style layer toggle
     */
    _handleStyleLayerToggle(layer, isChecked) {
        const styleLayers = this._map.getStyle().layers;
        const layersToToggle = styleLayers
            .filter(styleLayer => styleLayer['source-layer'] === layer.sourceLayer)
            .map(styleLayer => styleLayer.id);

        layersToToggle.forEach(layerId => {
            if (this._map.getLayer(layerId)) {
                this._map.setLayoutProperty(
                    layerId,
                    'visibility',
                    isChecked ? 'visible' : 'none'
                );
            }
        });
    }

    /**
     * Load legend image on-demand when layer is enabled and expanded
     */
    _loadLegendImageIfNeeded(groupElement, legendImageUrl) {
        if (!legendImageUrl || !groupElement) return;

        const $legendContainer = $(groupElement).find('.legend-container');
        if ($legendContainer.length === 0) return;

        // Check if already loaded
        const isLoaded = $legendContainer.attr('data-legend-loaded') === 'true';
        if (isLoaded) return;

        // Check if image is cached
        if (this._legendImageCache.has(legendImageUrl)) {
            const cachedContent = this._legendImageCache.get(legendImageUrl);
            $legendContainer.html(cachedContent);
            $legendContainer.attr('data-legend-loaded', 'true');
            return;
        }

        // Render and cache the legend image
        const legendContent = this._renderLegendImage(legendImageUrl);
        $legendContainer.html(legendContent);
        $legendContainer.attr('data-legend-loaded', 'true');

        // Cache the content for future use
        this._legendImageCache.set(legendImageUrl, legendContent);
    }

    /**
     * Render legend image (PDF or regular image)
     */
    _renderLegendImage(legendImageUrl) {
        if (!legendImageUrl) return '';

        if (legendImageUrl.toLowerCase().endsWith('.pdf')) {
            return `
                <div class="legend-pdf-container">
                    <a href="${legendImageUrl}" target="_blank" class="pdf-legend-link">
                        <sl-icon name="file-earmark-pdf" style="color: red; font-size: 1.5rem;"></sl-icon>
                        <span>View Legend PDF</span>
                    </a>
                </div>
            `;
        } else {
            // Use loading="lazy" for better performance, but still load when needed
            return `<img src="${legendImageUrl}" alt="Legend" class="legend-image" loading="lazy">`;
        }
    }

    /**
     * Initialize with animation
     */
    _initializeWithAnimation() {
        if (!this._container) {
            console.warn('[MapLayerControl] Container not initialized yet, skipping animation');
            return;
        }

        const allToggles = this._container.querySelectorAll('.group-header .toggle-switch input[type="checkbox"]');
        const groupHeaders = Array.from(allToggles).filter(toggle =>
            !toggle.closest('.layer-controls')
        );

        groupHeaders.forEach((toggleInput, index) => {
            const group = this._state.groups[index];
            const shouldBeChecked = group?.initiallyChecked ?? false;
            toggleInput.checked = shouldBeChecked;

            void toggleInput.offsetHeight;
            const toggleSlider = toggleInput.nextElementSibling;
            if (toggleSlider && toggleSlider.classList.contains('toggle-slider')) {
                void toggleSlider.offsetHeight;
            }

            toggleInput.dispatchEvent(new Event('change'));
        });

        if (!this._initialized) {
            this._container.classList.add('no-transition');
            void this._container.offsetWidth;
            this._container.classList.remove('no-transition');
            this._initialized = true;
        }

        requestAnimationFrame(() => {
            this._container.classList.add('collapsed');
        });
    }

    /**
     * Fly to location using geocoding
     */
    async _flyToLocation(location) {
        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(location)}.json?access_token=${mapboxgl.accessToken}&country=in`
            );
            const data = await response.json();

            if (data.features && data.features.length > 0) {
                const [lng, lat] = data.features[0].center;
                this._map.flyTo({
                    center: [lng, lat],
                    zoom: 12,
                    duration: 2000
                });
            }
        } catch (error) {
            console.error('Error flying to location:', error);
        }
    }

    /**
     * Set state manager reference
     */
    setStateManager(stateManager) {
        this._stateManager = stateManager;
        // Register all active layers immediately
        this._registerAllActiveLayers();
    }

    /**
     * Register all active layers with state manager
     */
    _registerAllActiveLayers() {
        if (!this._stateManager) return;

        this._state.groups.forEach(group => {
            if (group.initiallyChecked) {
                this._registerLayerWithStateManager(group);
            }
        });
    }

    /**
     * Register layer with state manager
     */
    _registerLayerWithStateManager(layerConfig) {
        if (!this._stateManager) return;

        // Resolve _sourceAtlas and tags from registry
        if (layerConfig.id && window.layerRegistry) {
            const resolvedLayer = window.layerRegistry.getLayer(layerConfig.id);

            if (resolvedLayer) {
                const updates = {};

                if (!layerConfig._sourceAtlas && resolvedLayer._sourceAtlas) {
                    updates._sourceAtlas = resolvedLayer._sourceAtlas;
                    layerConfig._sourceAtlas = resolvedLayer._sourceAtlas;
                }

                // Always merge tags from registry (registry has cascaded tags from atlas)
                if (resolvedLayer.tags) {
                    if (!layerConfig.tags) {
                        updates.tags = resolvedLayer.tags;
                        layerConfig.tags = resolvedLayer.tags;
                    } else if (Array.isArray(layerConfig.tags) && Array.isArray(resolvedLayer.tags)) {
                        // Merge tags if both exist
                        const mergedTags = [...new Set([...layerConfig.tags, ...resolvedLayer.tags])];
                        if (JSON.stringify(layerConfig.tags) !== JSON.stringify(mergedTags)) {
                            updates.tags = mergedTags;
                            layerConfig.tags = mergedTags;
                        }
                    }
                }

                // Update in state groups array if present
                if (Object.keys(updates).length > 0) {
                    const groupIndex = this._state.groups.findIndex(g => g.id === layerConfig.id);
                    if (groupIndex !== -1) {
                        Object.assign(this._state.groups[groupIndex], updates);
                    }
                }
            }
        }

        // Register layer attribution if available
        // We do this BEFORE potentially skipping style layers for state management
        if (layerConfig.attribution && window.attributionControl) {
            if (layerConfig.type === 'style' || layerConfig.type === 'raster-style-layer') {
                // For style layers, we need to register attribution for all actual map layers
                // This ensures attribution shows up even if the config ID doesn't match the style ID
                if (this._mapboxAPI) {
                    const layerIds = this._mapboxAPI.getLayerGroupIds(layerConfig.id, layerConfig);
                    layerIds.forEach(id => {
                        window.attributionControl.addLayerAttribution(id, layerConfig.attribution, layerConfig.title, layerConfig);
                    });
                }
            } else {
                // Standard registration for other layer types
                window.attributionControl.addLayerAttribution(layerConfig.id, layerConfig.attribution, layerConfig.title, layerConfig);
            }
        }

        // Skip style layers as they don't have their own sources/features
        if (layerConfig.type === 'style') {
            return;
        }

        // Skip layers without a type - they can't be properly handled
        if (!layerConfig.type) {
            return;
        }

        // Skip vector layers that explicitly disable interactivity via inspect: false or inspect: null
        const isVectorLike = layerConfig.type === 'geojson' || layerConfig.type === 'vector' || layerConfig.type === 'csv' || layerConfig.type === 'sheet' || layerConfig.type === 'js';
        const isExplicitlyDisabled = layerConfig.inspect === false || layerConfig.inspect === null;
        if (isVectorLike && isExplicitlyDisabled) {
            return;
        }

        // Register the layer - MapFeatureStateManager will handle raster vs vector distinction
        this._stateManager.registerLayer(layerConfig);
    }

    /**
     * Unregister layer with state manager
     */
    _unregisterLayerWithStateManager(layerId) {
        if (this._stateManager) {
            this._stateManager.unregisterLayer(layerId);
        }

        // Remove layer attribution
        if (window.attributionControl) {
            // Check if we need to remove attribution for multiple layer IDs (style layers)
            // We need to look up the group from state to know the type
            const group = this._state.groups.find(g => g.id === layerId);

            if (group && (group.type === 'style' || group.type === 'raster-style-layer') && this._mapboxAPI) {
                const layerIds = this._mapboxAPI.getLayerGroupIds(layerId, group);
                layerIds.forEach(id => {
                    window.attributionControl.removeLayerAttribution(id);
                });
            } else {
                window.attributionControl.removeLayerAttribution(layerId);
            }
        }
    }

    /**
     * Save layer settings
     */
    _saveLayerSettingsInternal(newConfig) {
        try {
            const groupIndex = this._state.groups.findIndex(g => g.id === newConfig.id);
            if (groupIndex === -1) {
                throw new Error('Could not find layer configuration to update');
            }

            const newGroups = [...this._state.groups];
            newGroups[groupIndex] = newConfig;

            this._updateState({ groups: newGroups });
        } catch (error) {
            console.error('Error saving layer settings:', error);
            alert('Failed to save layer settings. Please check the console for details.');
        }
    }

    /**
     * Show toast notification
     */
    _showToast(message, type = 'success', duration = 3000) {
        let toast = document.querySelector('.toast-notification');
        if (!toast) {
            toast = document.createElement('div');
            toast.className = 'toast-notification';
            document.body.appendChild(toast);
        }

        toast.textContent = message;
        toast.classList.remove('success', 'error', 'info');
        toast.classList.add(type);

        requestAnimationFrame(() => {
            toast.classList.add('show');
            setTimeout(() => {
                toast.classList.remove('show');
                setTimeout(() => toast.remove(), 300);
            }, duration);
        });
    }

    /**
     * Set up drawer focus management to prevent aria-hidden accessibility issues
     * Blurs focused elements inside the drawer before it's hidden
     */
    _setupDrawerFocusManagement() {
        // Wait for drawer to be available
        const findDrawer = () => {
            const drawer = document.querySelector('#map-controls-drawer');
            if (drawer) {
                // Listen for drawer hide event (fires before hiding)
                drawer.addEventListener('sl-hide', () => {
                    // Check if the currently focused element is inside the drawer
                    const activeElement = document.activeElement;
                    if (activeElement && drawer.contains(activeElement)) {
                        // Blur the focused element to prevent aria-hidden accessibility violation
                        activeElement.blur();
                    }
                });
            } else {
                // Retry if drawer not found yet
                setTimeout(findDrawer, 100);
            }
        };
        findDrawer();
    }

    /**
     * Initialize filter controls
     */
    _initializeFilterControls() {
        requestAnimationFrame(() => {
            const searchInput = document.getElementById('layer-search-input');
            const atlasFilterBtn = document.getElementById('atlas-filter-select');
            const atlasFilterText = document.getElementById('atlas-filter-text');
            const atlasViewLocationBtn = document.getElementById('atlas-view-location-btn');

            // Initialize search input
            if (searchInput) {
                // Update placeholder with layer count
                this._updateSearchPlaceholder();

                // Add margin to search icon
                const searchIcon = searchInput.querySelector('sl-icon[slot="prefix"]');
                if (searchIcon) {
                    searchIcon.style.marginLeft = '10px';
                }

                searchInput.addEventListener('sl-input', (e) => {
                    this._applyAllFilters();
                });
                searchInput.addEventListener('sl-clear', () => {
                    this._applyAllFilters();
                });
            }

            // Initialize atlas filter
            if (atlasFilterBtn) {
                // Set initial text to current atlas name
                this._updateAtlasButtonText();

                // Defer dropdown creation until first click (lazy loading)
                atlasFilterBtn.addEventListener('click', () => {
                    if (!atlasFilterBtn.hasAttribute('data-dropdown-initialized')) {
                        atlasFilterBtn.setAttribute('data-dropdown-initialized', 'true');
                        requestAnimationFrame(() => {
                            this._createAtlasDropdownMenu(atlasFilterBtn);
                        });
                    }
                }, { once: true });
            }

            // Initialize View Location button
            if (atlasViewLocationBtn) {
                atlasViewLocationBtn.addEventListener('click', () => {
                    if (this._selectedAtlasFilter) {
                        this._navigateToAtlasLocation(this._selectedAtlasFilter);
                    }
                });
            }

            // Initialize filter checkboxes
            const filterActiveMaps = document.getElementById('filter-active-maps');
            const filterMapsInView = document.getElementById('filter-maps-in-view');

            if (filterActiveMaps) {
                this._filterActiveMaps = !!filterActiveMaps.checked;

                filterActiveMaps.addEventListener('sl-change', (e) => {
                    this._filterActiveMaps = !!e.target.checked;
                    this._applyAllFilters();
                });

                const filterActiveMapsLabel = filterActiveMaps.closest('label');
                if (filterActiveMapsLabel) {
                    filterActiveMapsLabel.addEventListener('click', (e) => {
                        if (e.target.tagName !== 'SL-CHECKBOX') {
                            e.preventDefault();
                            filterActiveMaps.checked = !filterActiveMaps.checked;
                            this._filterActiveMaps = !!filterActiveMaps.checked;
                            this._applyAllFilters();
                        }
                    });
                }
            }

            if (filterMapsInView) {
                this._filterMapsInView = !!filterMapsInView.checked;

                filterMapsInView.addEventListener('sl-change', (e) => {
                    this._filterMapsInView = !!e.target.checked;
                    this._applyAllFilters();

                    if (e.target.checked) {
                        this._addMapMoveListener();
                    }
                });

                const filterMapsInViewLabel = filterMapsInView.closest('label');
                if (filterMapsInViewLabel) {
                    filterMapsInViewLabel.addEventListener('click', (e) => {
                        if (e.target.tagName !== 'SL-CHECKBOX') {
                            e.preventDefault();
                            filterMapsInView.checked = !filterMapsInView.checked;
                            this._filterMapsInView = !!filterMapsInView.checked;
                            this._applyAllFilters();

                            if (filterMapsInView.checked) {
                                this._addMapMoveListener();
                            }
                        }
                    });
                }

                if (this._filterMapsInView) {
                    this._addMapMoveListener();
                    this._applyFiltersWhenReady();
                }
            }
        });
    }

    /**
     * Apply filters when map and registry are ready
     */
    _applyFiltersWhenReady() {
        const checkReady = () => {
            if (this._map && this._map.isStyleLoaded() && window.layerRegistry && window.layerRegistry._initialized) {
                this._applyAllFilters();
            } else {
                setTimeout(checkReady, 100);
            }
        };
        checkReady();
    }

    /**
     * Add map move listener to update filters on viewport change
     */
    _addMapMoveListener() {
        if (this._mapMoveListenerAdded || !this._map) return;

        const updateFiltersOnMove = () => {
            if (this._filterMapsInView) {
                this._applyAllFilters();
            }
        };

        this._map.on('moveend', updateFiltersOnMove);
        this._mapMoveListenerAdded = true;
    }

    /**
     * Get bounds for a layer (from layer config or parent atlas)
     */
    _getLayerBounds(layer) {
        if (layer.bounds) {
            return this._normalizeBounds(layer.bounds);
        }

        if (layer.bbox) {
            return this._normalizeBounds(layer.bbox);
        }

        const atlasId = this._getAtlasIdForLayer(layer);
        if (atlasId && window.layerRegistry) {
            const atlasMetadata = window.layerRegistry.getAtlasMetadata(atlasId);
            if (atlasMetadata) {
                if (atlasMetadata.bounds) {
                    return this._normalizeBounds(atlasMetadata.bounds);
                }
                if (atlasMetadata.bbox) {
                    return this._normalizeBounds(atlasMetadata.bbox);
                }
            }
        }

        return null;
    }

    /**
     * Normalize bounds to array format [west, south, east, north]
     */
    _normalizeBounds(bounds) {
        if (!bounds) return null;

        if (typeof bounds === 'string') {
            const parts = bounds.split(',').map(s => parseFloat(s.trim()));
            if (parts.length === 4 && parts.every(n => !isNaN(n))) {
                return parts;
            }
            return null;
        }

        if (Array.isArray(bounds) && bounds.length === 4) {
            return bounds;
        }

        if (bounds.type === 'Polygon' || bounds.type === 'MultiPolygon') {
            return bounds;
        }

        return null;
    }

    /**
     * Check if layer bounds intersect with current viewport
     */
    _boundsIntersectViewport(layerBounds) {
        if (!this._map) return true;

        if (!layerBounds) return false;

        const mapBounds = this._map.getBounds();
        const mapNorth = mapBounds.getNorth();
        const mapSouth = mapBounds.getSouth();
        const mapEast = mapBounds.getEast();
        const mapWest = mapBounds.getWest();

        let layerWest, layerSouth, layerEast, layerNorth;

        if (Array.isArray(layerBounds)) {
            if (layerBounds.length === 4) {
                [layerWest, layerSouth, layerEast, layerNorth] = layerBounds;
            } else {
                return false;
            }
        } else if (layerBounds.type === 'Polygon' || layerBounds.type === 'MultiPolygon') {
            const coords = layerBounds.type === 'Polygon'
                ? layerBounds.coordinates[0]
                : layerBounds.coordinates.flat(1);

            const lngs = coords.map(c => c[0]);
            const lats = coords.map(c => c[1]);
            layerWest = Math.min(...lngs);
            layerEast = Math.max(...lngs);
            layerSouth = Math.min(...lats);
            layerNorth = Math.max(...lats);
        } else {
            return false;
        }

        const intersects = !(
            layerEast < mapWest ||
            layerWest > mapEast ||
            layerNorth < mapSouth ||
            layerSouth > mapNorth
        );

        return intersects;
    }

    /**
     * Update the atlas button text to show current atlas or default
     */
    _updateAtlasButtonText() {
        const atlasFilterText = document.getElementById('atlas-filter-text');
        if (!atlasFilterText || !window.layerRegistry) return;

        const currentAtlas = window.layerRegistry._currentAtlas || 'index';
        const atlasMetadata = window.layerRegistry.getAtlasMetadata(currentAtlas);

        if (this._selectedAtlasFilter) {
            // Show selected filter atlas
            const selectedMetadata = window.layerRegistry.getAtlasMetadata(this._selectedAtlasFilter);
            atlasFilterText.textContent = selectedMetadata?.name || this._selectedAtlasFilter;
        } else {
            // Show current atlas as default
            atlasFilterText.textContent = atlasMetadata?.name || 'All Atlases';
        }
    }

    /**
     * Update the search input placeholder with layer count
     */
    _updateSearchPlaceholder() {
        const searchInput = document.getElementById('layer-search-input');
        if (!searchInput) return;

        let layerCount = 0;

        if (this._selectedAtlasFilter && window.layerRegistry) {
            // Count layers in selected atlas
            const atlasLayers = window.layerRegistry.getAtlasLayers(this._selectedAtlasFilter);
            layerCount = atlasLayers.length;
        } else {
            // Count all layers in state
            layerCount = this._state.groups.length;
        }

        searchInput.placeholder = `Search from ${layerCount} maps...`;
    }

    /**
     * Create dropdown menu for atlas selection
     */
    _createAtlasDropdownMenu(buttonElement) {
        if (!window.layerRegistry || !window.layerRegistry._atlasMetadata) return;

        // Store parent node before any manipulation
        const parentNode = buttonElement.parentNode;

        // Create dropdown element
        const dropdown = document.createElement('sl-dropdown');
        dropdown.distance = 5;
        dropdown.placement = 'bottom-start';
        dropdown.style.width = '100%';

        // Create menu
        const menu = document.createElement('sl-menu');
        menu.style.minWidth = '200px';

        // Use DocumentFragment for batch DOM operations
        const fragment = document.createDocumentFragment();

        // Add divider
        const divider = document.createElement('sl-divider');
        fragment.appendChild(divider);

        // Get all atlases from the registry
        const atlases = Array.from(window.layerRegistry._atlasMetadata.entries());

        // Sort atlases alphabetically by name
        atlases.sort((a, b) => {
            const nameA = a[1].name || a[0];
            const nameB = b[1].name || b[0];
            return nameA.localeCompare(nameB);
        });

        // Add options for each atlas
        atlases.forEach(([atlasId, metadata]) => {
            const option = document.createElement('sl-menu-item');
            option.value = atlasId;

            // Add a colored badge using the atlas color
            if (metadata.color) {
                option.innerHTML = `
                    <span class="atlas-color-badge" style="background-color: ${metadata.color};" slot="prefix"></span>
                    <span>${metadata.name || atlasId}</span>
                `;
            } else {
                option.textContent = metadata.name || atlasId;
            }

            option.addEventListener('click', () => {
                this._selectedAtlasFilter = atlasId;
                this._updateAtlasButtonText();
                this._applyAllFilters();
                dropdown.hide();
            });

            fragment.appendChild(option);
        });

        // Append all options at once
        menu.appendChild(fragment);

        // Append menu to dropdown
        dropdown.appendChild(menu);

        // Set button as trigger and append to dropdown
        buttonElement.setAttribute('slot', 'trigger');
        dropdown.appendChild(buttonElement);

        // Insert dropdown into parent where button was
        parentNode.appendChild(dropdown);
    }

    /**
     * Apply all filters (search, hide inactive, and atlas) - uses layer registry for cross-atlas search
     */
    _applyAllFilters() {
        try {
            if (!this._container || !window.layerRegistry) return;

            const searchInput = document.getElementById('layer-search-input');

            const searchTerm = searchInput ? searchInput.value.toLowerCase().trim() : '';
            const selectedAtlas = this._selectedAtlasFilter || '';
            const isSearching = searchTerm.length > 0;
            const isAtlasFiltering = selectedAtlas.length > 0;

            const layerGroups = this._container.querySelectorAll('.group-header');

            // If searching, use the layer registry to find cross-atlas matches
            let crossAtlasResults = [];
            if (isSearching) {
                const currentAtlas = window.layerRegistry._currentAtlas;
                crossAtlasResults = window.layerRegistry.searchLayers(searchTerm, currentAtlas);
            }

            // If atlas filtering, get all layers from the selected atlas
            let atlasLayers = [];
            if (isAtlasFiltering) {
                atlasLayers = window.layerRegistry.getAtlasLayers(selectedAtlas);
            }

            // Get current atlas layer IDs for deduplication
            const currentLayerIds = new Set();
            this._state.groups.forEach(group => {
                currentLayerIds.add(group.id);
            });

            let visibleCount = 0;
            let hiddenCount = 0;

            // Apply visibility to existing layers
            layerGroups.forEach(groupElement => {
                const groupId = groupElement.getAttribute('data-layer-id');
                if (!groupId) return;

                // Find group data
                const groupData = this._state.groups.find(g => g.id === groupId);
                if (!groupData) return;

                const searchMatches = this._layerMatchesSearch(groupData, searchTerm);

                // Check if layer matches atlas filter
                let atlasMatches = true;
                if (isAtlasFiltering) {
                    const atlasId = this._getAtlasIdForLayer(groupData);
                    atlasMatches = atlasId === selectedAtlas;
                }

                // Check if layer matches active maps filter
                let activeMatches = true;
                if (this._filterActiveMaps) {
                    const toggleInput = groupElement.querySelector('.toggle-switch input[type="checkbox"]');
                    activeMatches = toggleInput && toggleInput.checked;
                }

                // Check if layer matches maps in view filter
                let viewMatches = true;
                if (this._filterMapsInView) {
                    const layerBounds = this._getLayerBounds(groupData);
                    viewMatches = this._boundsIntersectViewport(layerBounds);
                }

                // Show if matches all filters
                const shouldShow = searchMatches && atlasMatches && activeMatches && viewMatches;
                groupElement.style.display = shouldShow ? '' : 'none';

                if (shouldShow) {
                    visibleCount++;
                } else {
                    hiddenCount++;
                }
            });

            // Add cross-atlas search results dynamically (if not already in current atlas)
            if (isSearching && crossAtlasResults.length > 0) {
                this._showCrossAtlasSearchResults(crossAtlasResults, currentLayerIds);
            } else if (isAtlasFiltering && atlasLayers.length > 0) {
                // Show all layers from the selected atlas
                this._showAtlasFilterResults(atlasLayers, selectedAtlas, currentLayerIds);
            } else {
                this._hideCrossAtlasSearchResults();
            }
        } catch (error) {
        }
    }

    /**
     * Check if a layer matches the search term
     */
    _layerMatchesSearch(groupData, searchTerm) {
        if (!searchTerm) return true;

        return (groupData.id && groupData.id.toLowerCase().includes(searchTerm)) ||
            (groupData.name && groupData.name.toLowerCase().includes(searchTerm)) ||
            (groupData.title && groupData.title.toLowerCase().includes(searchTerm)) ||
            (groupData.description && groupData.description.toLowerCase().includes(searchTerm)) ||
            (groupData.tags && Array.isArray(groupData.tags) &&
                groupData.tags.some(tag => tag && tag.toLowerCase().includes(searchTerm)));
    }

    /**
     * Show cross-atlas search results
     */
    _showCrossAtlasSearchResults(results, currentLayerIds) {
        // Check if we already have a cross-atlas container
        let $crossAtlasContainer = $(this._container).find('.cross-atlas-results');

        if ($crossAtlasContainer.length === 0) {
            // Create container for cross-atlas results
            $crossAtlasContainer = $('<div>', {
                class: 'cross-atlas-results mt-4 border-t-2 border-gray-700 pt-4'
            });
            $(this._container).append($crossAtlasContainer);
        }

        // Clear existing results
        $crossAtlasContainer.empty();

        // Add header
        $crossAtlasContainer.append($('<div>', {
            class: 'text-sm text-gray-400 mb-2 px-2',
            text: 'From other atlases:'
        }));

        // Add each result (skipping duplicates)
        results.forEach(layer => {
            // Skip if already in current atlas
            if (currentLayerIds.has(layer.id)) {
                return;
            }

            // Create layer element with cross-atlas styling
            const $layerElement = this._createCrossAtlasLayerElement(layer);
            $crossAtlasContainer.append($layerElement);
        });
    }

    /**
     * Show atlas filter results - displays all layers from selected atlas
     */
    _showAtlasFilterResults(layers, atlasId, currentLayerIds) {
        // Check if we already have a cross-atlas container
        let $crossAtlasContainer = $(this._container).find('.cross-atlas-results');

        if ($crossAtlasContainer.length === 0) {
            // Create container for cross-atlas results
            $crossAtlasContainer = $('<div>', {
                class: 'cross-atlas-results mt-4 border-t-2 border-gray-700 pt-4'
            });
            $(this._container).append($crossAtlasContainer);
        }

        // Clear existing results
        $crossAtlasContainer.empty();

        // Get atlas metadata for header
        const atlasMetadata = window.layerRegistry.getAtlasMetadata(atlasId);
        const atlasName = atlasMetadata?.name || atlasId;
        const atlasColor = atlasMetadata?.color || '#2563eb';

        // Get atlas configuration to access map location
        const atlasLayers = window.layerRegistry.getAtlasLayers(atlasId);
        const atlasConfig = atlasLayers.length > 0 ? atlasLayers[0] : null;

        // Add header with atlas name, color, and View Location button
        const $header = $('<div>', {
            class: 'text-sm text-gray-400 mb-2 px-2 flex items-center gap-2 justify-between'
        });

        const $leftSection = $('<div>', {
            class: 'flex items-center gap-2',
            html: `
                <span class="atlas-color-badge" style="background-color: ${atlasColor};"></span>
                <span>Layers from ${atlasName}:</span>
            `
        });

        const $viewLocationBtn = $('<button>', {
            class: 'atlas-view-location-btn',
            html: '<sl-icon name="geo-alt" style="font-size: 12px; margin-right: 4px;"></sl-icon><span>View Location</span>'
        });

        // Add click handler to navigate to atlas location
        $viewLocationBtn.on('click', () => {
            this._navigateToAtlasLocation(atlasId);
        });

        $header.append($leftSection, $viewLocationBtn);
        $crossAtlasContainer.append($header);

        // Add each layer from the atlas (skipping duplicates)
        layers.forEach(layer => {
            // Resolve the layer to get full details
            const resolvedLayer = window.layerRegistry.getLayer(layer.id, atlasId);
            if (!resolvedLayer) return;

            // Skip if already in current atlas (visible in main list)
            const layerIdToCheck = resolvedLayer._originalId || resolvedLayer.id || layer.id;
            if (currentLayerIds.has(layerIdToCheck)) {
                return;
            }

            // Create layer element with cross-atlas styling
            const $layerElement = this._createCrossAtlasLayerElement(resolvedLayer);
            $crossAtlasContainer.append($layerElement);
        });
    }

    /**
     * Hide cross-atlas search results
     */
    _hideCrossAtlasSearchResults() {
        const $crossAtlasContainer = $(this._container).find('.cross-atlas-results');
        $crossAtlasContainer.remove();
    }

    /**
     * Create a layer element for cross-atlas search results
     */
    _createCrossAtlasLayerElement(layer) {
        const $groupHeader = $('<sl-details>', {
            class: 'group-header w-full map-controls-group cross-atlas-layer',
            'data-layer-id': layer._prefixedId || layer.id
        });

        // Create summary section
        const $summary = $('<div>', {
            slot: 'summary',
            class: 'flex items-center relative w-full h-12 bg-gray-800 opacity-75'
        });

        const $contentWrapper = $('<div>', {
            class: 'flex items-center gap-2 relative z-10 w-full p-2'
        });

        // Add toggle and title
        const $toggleLabel = $('<label>', { class: 'toggle-switch' });
        const $toggleInput = $('<input>', { type: 'checkbox', checked: false });
        const $toggleSlider = $('<span>', { class: 'toggle-slider' });
        $toggleLabel.append($toggleInput, $toggleSlider);

        const $titleSpan = $('<span>', {
            text: layer.title || layer.id,
            class: 'control-title text-sm font-medium text-white'
        });

        // Add atlas badge
        const displayAtlasName = layer._sourceAtlas.charAt(0).toUpperCase() + layer._sourceAtlas.slice(1);

        // Get color from registry, fallback to blue-600 if not available
        let badgeColor = '#2563eb'; // Default blue
        if (window.layerRegistry) {
            badgeColor = window.layerRegistry.getAtlasColor(layer._sourceAtlas);
        }

        const $atlasBadge = $('<span>', {
            class: 'text-xs text-white px-2 py-1 rounded ml-auto cursor-pointer hover:opacity-80 transition-opacity',
            style: `background-color: ${badgeColor};`,
            text: displayAtlasName,
            title: `Switch to ${displayAtlasName} atlas`
        });

        // Make badge clickable to navigate to atlas
        $atlasBadge.on('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._navigateToAtlas(layer._sourceAtlas);
        });

        $contentWrapper.append($toggleLabel, $titleSpan, $atlasBadge);

        // Add header background if exists (resolve from layer data)
        let headerImage = layer.headerImage;
        if (!headerImage && layer.id && window.layerRegistry) {
            const resolvedLayer = window.layerRegistry.getLayer(layer._prefixedId || layer.id);
            if (resolvedLayer && resolvedLayer.headerImage) {
                headerImage = resolvedLayer.headerImage;
            }
        }

        if (headerImage) {
            const $headerBg = $('<div>', {
                class: 'absolute top-0 left-0 right-0 w-full h-full bg-cover bg-center bg-no-repeat',
                style: `background-image: url('${headerImage}')`
            });

            const $headerOverlay = $('<div>', {
                class: 'absolute top-0 left-0 right-0 w-full h-full bg-black bg-opacity-40'
            });

            $summary.append($headerBg, $headerOverlay, $contentWrapper);
        } else {
            $summary.append($contentWrapper);
        }

        $groupHeader.append($summary);

        // Add description if available
        if (layer.description) {
            const $description = $('<div>', {
                class: 'text-sm text-gray-600 p-2',
                html: layer.description
            });
            $groupHeader.append($description);
        }

        // Set up event handlers
        $groupHeader[0].addEventListener('sl-show', async () => {
            $toggleInput.prop('checked', true);
            // Add layer to state and activate it
            await this._addCrossAtlasLayer(layer);
        });

        $groupHeader[0].addEventListener('sl-hide', () => {
            $toggleInput.prop('checked', false);
            // Remove layer from state
            this._removeCrossAtlasLayer(layer._prefixedId || layer.id);
        });

        return $groupHeader;
    }

    /**
     * Add a cross-atlas layer to the active state
     */
    async _addCrossAtlasLayer(layer) {
        // Add to state with prefixed ID
        const layerWithPrefix = {
            ...layer,
            id: layer._prefixedId || layer.id,
            initiallyChecked: true
        };

        // Set the normalized ID for URL serialization using the registry
        if (window.layerRegistry) {
            layerWithPrefix._normalizedId = window.layerRegistry.normalizeLayerId(layerWithPrefix.id);
        }

        let insertPosition;
        if (LayerOrderManager.isBasemap(layerWithPrefix)) {
            insertPosition = this._state.groups.findIndex(g => LayerOrderManager.isBasemap(g));
            if (insertPosition === -1) {
                insertPosition = this._state.groups.length;
            }
        } else {
            insertPosition = 0;
        }

        this._state.groups.splice(insertPosition, 0, layerWithPrefix);

        // Activate the layer
        if (this._mapboxAPI) {
            await this._mapboxAPI.createLayerGroup(layerWithPrefix.id, layerWithPrefix, { visible: true });
        }

        // Register with state manager if available
        if (this._stateManager) {
            this._registerLayerWithStateManager(layerWithPrefix);
        }

        // Update attribution after layer is added
        if (window.attributionControl) {
            window.attributionControl._updateAttribution();
        }

        // Update URL to reflect the change
        if (window.urlManager) {
            window.urlManager.onLayersChanged();
        }

        // Dispatch custom event for URL sync
        window.dispatchEvent(new CustomEvent('layer-toggled', {
            detail: { layerId: layerWithPrefix.id, visible: true, isCrossAtlas: true }
        }));
    }

    /**
     * Navigate to a different atlas
     */
    _navigateToAtlas(atlasId) {
        if (!atlasId) return;

        const url = new URL(window.location.href);
        url.searchParams.set('atlas', atlasId);

        // Navigate to the new URL
        window.location.href = url.toString();
    }

    /**
     * Navigate map to the atlas's defined location
     */
    _navigateToAtlasLocation(atlasId) {
        if (!atlasId || !this._map) return;

        // Fetch the atlas configuration to get map location
        fetch(`config/${atlasId}.atlas.json`)
            .then(response => response.json())
            .then(config => {
                if (config.map) {
                    const { center, zoom } = config.map;
                    if (center && zoom !== undefined) {
                        // Animate to the atlas location
                        this._map.flyTo({
                            center: center,
                            zoom: zoom,
                            duration: 2000,
                            essential: true
                        });
                    }
                }
            })
            .catch(error => {
                console.error(`Failed to load atlas configuration for ${atlasId}:`, error);
            });
    }

    /**
     * Remove a cross-atlas layer from the active state
     */
    _removeCrossAtlasLayer(layerId) {
        // Find and save the layer before removing it
        const index = this._state.groups.findIndex(g => g.id === layerId || g._prefixedId === layerId);
        if (index === -1) {
            console.warn(`[LayerControl] Layer ${layerId} not found in state for removal`);
            return;
        }

        // Save layer reference before removing from array
        const layer = this._state.groups[index];

        // Hide the layer first
        if (this._mapboxAPI && layer) {
            this._mapboxAPI.updateLayerGroupVisibility(layerId, layer, false);
        }

        // Now remove from state
        this._state.groups.splice(index, 1);

        // Unregister with state manager if available
        if (this._stateManager) {
            this._unregisterLayerWithStateManager(layerId);
        }

        // Update attribution after layer removal
        if (window.attributionControl) {
            window.attributionControl._updateAttribution();
        }

        // Update URL to reflect the change
        if (window.urlManager) {
            window.urlManager.onLayersChanged();
        }

        // Dispatch custom event for URL sync
        window.dispatchEvent(new CustomEvent('layer-toggled', {
            detail: { layerId: layerId, visible: false, isCrossAtlas: true }
        }));
    }

    static isMapwarperMosaicUrl(url) {
        return MapWarperAPI.isMosaicUrl(url);
    }

    static extractMapwarperMosaicId(url) {
        return MapWarperAPI.extractMosaicId(url);
    }

    static async fetchMapwarperMosaicMetadata(mosaicId, baseUrl = 'https://mapwarper.net') {
        return MapWarperAPI.fetchMosaicMetadata(mosaicId, baseUrl);
    }

    static createConfigFromMapwarperMosaic(mosaicData, baseUrl = 'https://mapwarper.net') {
        return MapWarperAPI.createMosaicConfig(mosaicData, baseUrl);
    }

    static async createConfigFromMapwarperUrl(url) {
        return MapWarperAPI.createConfigFromUrl(url);
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        if (this._mapboxAPI) {
            this._mapboxAPI.cleanup();
        }
    }
}